import { Component, OnInit } from "@angular/core";
import { FormGroup, FormBuilder, Validators } from "@angular/forms";

import { UserWizardService } from "../user-wizard.service";
import { ValidationService } from "../../../../common/validation/validation.service";
import { AbstractWizardStepComponent } from "../../../wizard-dialog/abstract-wizard-step.component";
import { MatSlideToggleChange } from "@angular/material";
import { SdpIcon } from "app/common/icon/models";
import { UserCreateRequest } from "@impulse/sdp-shared-models";

@Component({
	templateUrl: "./user-wizard-edit.component.html"
})
export class UserWizardEditComponent extends AbstractWizardStepComponent<void> implements OnInit {
	public form: FormGroup;
	public isCreate = this.service.isCreate;
	public isEnabled: boolean;
	public isAdminEnabled: boolean;
	public adminDisabledIcon: SdpIcon.Icon = {
		type: "fa",
		name: "warning",
		cssClass: "sdp-user-edit-admin-disabled-icon",
		toolTip: "user.edit.admin.disabled"
	};

	constructor(
		private service: UserWizardService,
		private formBuilder: FormBuilder,
		private validationService: ValidationService
	) {
		super();

		// The data model in user has a "disabled" property, we use the opposite "enabled" in the UI
		this.isEnabled = !this.service.getValue("disabled", false);
		this.isAdminEnabled = this.service.getValue("admin", false);

		this.form = this.formBuilder.group({
			email: [
				{
					value: this.service.getValue("email", ""),
					disabled: !this.isCreate
				},
				Validators.compose([Validators.required, this.validationService.email])
			],
			firstName: [
				this.service.getValue("firstName", ""),
				Validators.compose([Validators.required, this.validationService.simpleStringWithSpaces])
			],
			lastName: [
				this.service.getValue("lastName", ""),
				Validators.compose([Validators.required, this.validationService.simpleStringWithSpaces])
			],
			admin: [
				this.isAdminEnabled,
				false
			],
			enabled: [
				this.isEnabled,
				false
			]
		});
	}

	public ngOnInit() {
		this.form.updateValueAndValidity();
	}

	public get isPrimary() {
		return this.service.isPrimary;
	}

	public get isCurrentUser() {
		return this.service.isCurrentUser;
	}

	public get title() {
		return this.isCreate ? "user.wizard.userEdit.title.create" : "user.wizard.userEdit.title.update";
	}

	public get description() {
		return "user.wizard.edit.description";
	}

	public get nextStep(): AbstractWizardStepComponent.ConstructorFunction<void> | undefined {
		return undefined;
	}

	public get previousStep(): AbstractWizardStepComponent.ConstructorFunction<void> | undefined {
		return undefined;
	}

	public get isFormValid() {
		return this.form && this.form.valid;
	}

	public performPrevious() {
		this.saveUser();
	}

	public async performNext() {
		if (this.form.valid) {
			this.saveUser();
			await this.service.processGroupsToUsersSubmit();
		}
	}

	public get showProgressBar() {
		return false;
	}

	// we need to change the disabled attribute of the isPrimary field
	// when we change the state of the disabled field.
	// the form field is not easily reachable.
	public toggleEnabled(event: MatSlideToggleChange) {
		if (event.checked) {
			this.isEnabled = true;
		} else {
			this.isEnabled = false;
		}
	}

	public toggleAdmin(event: MatSlideToggleChange) {
		if (event.checked) {
			this.isAdminEnabled = true;
		} else {
			this.isAdminEnabled = false;
		}
	}

	private saveUser() {
		if (this.form !== undefined) {
			const rawValue = this.form.getRawValue();
			const userCreateRequest: UserCreateRequest = {
				email: rawValue.email,
				firstName: rawValue.firstName,
				lastName: rawValue.lastName,
				admin: rawValue.admin,
				disabled: !rawValue.enabled
			};
			this.service.processUserWizardEdit(userCreateRequest);
		}
	}
}
